import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useState, useEffect, useCallback, useMemo, useRef, type CSSProperties, type MutableRefObject, type ReactNode } from 'react';
import * as echarts from 'echarts';
import { Maximize2, Minimize2 } from 'lucide-react';
import {
  siteDataApi,
  type CostTypePeriod,
  type CostTypeParams,
  type CostVPivotRow,
  type CostPieRow,
  type CostType2Breakdown,
} from '../api/siteData';
import { ReportExportButton } from '../components/ReportExportButton';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { MultiSelect } from '../components/MultiSelect';
import { getCurrentCalendarRange, MarketingDateRangePicker } from '../components/MarketingTimeRangeFilter';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { DimensionHeaderTooltip, TruncatedValueTooltip } from '../components/DimensionHeaderTooltip';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { SortableHeader, sortRows, type SortState } from '../components/SortableTable';
import { useSiteShopName } from '../hooks/useSiteShopName';
import { useLazySection } from '../hooks/useLazySection';
import { useAppStore } from '../store/useAppStore';
import { formatSiteMetric, isSiteSensitiveMetric } from '../lib/siteDataSecurity';

const fmtNum = (v: number) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtPct = (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%');

function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function rangeFor(period: CostTypePeriod): { start: string; end: string } {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  if (period === 'month') return { start: fmtLocalDate(new Date(y, m, 1)), end: fmtLocalDate(new Date(y, m + 1, 0)) };
  if (period === 'year') return { start: fmtLocalDate(new Date(y, 0, 1)), end: fmtLocalDate(new Date(y, 11, 31)) };
  const start = new Date(today); start.setDate(start.getDate() - 6);
  return { start: fmtLocalDate(start), end: fmtLocalDate(today) };
}

function dayPresets(): { key: string; label: string; start: string; end: string }[] {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const last7s = new Date(today); last7s.setDate(last7s.getDate() - 6);
  return [
    { key: 'yesterday', label: '昨天', start: fmtLocalDate(yest), end: fmtLocalDate(yest) },
    { key: 'recent7', label: '最近7天', start: fmtLocalDate(last7s), end: fmtLocalDate(today) },
    { key: 'thisWeek', label: '本周', ...getCurrentCalendarRange('thisWeek', fmtLocalDate(today)) },
    { key: 'thisMonth', label: '本月', start: fmtLocalDate(new Date(y, m, 1)), end: fmtLocalDate(new Date(y, m + 1, 0)) },
    { key: 'lastMonth', label: '上月', start: fmtLocalDate(new Date(y, m - 1, 1)), end: fmtLocalDate(new Date(y, m, 0)) },
    { key: 'thisYear', label: '本年', start: fmtLocalDate(new Date(y, 0, 1)), end: fmtLocalDate(new Date(y, 11, 31)) },
    { key: 'custom', label: '自定义', start: '', end: '' },
  ];
}

const PERIOD_TABS: { key: CostTypePeriod; label: string }[] = [
  { key: 'day', label: '日' },
  { key: 'month', label: '月' },
  { key: 'year', label: '年' },
];

type PeriodPreset = 'current' | 'previous' | 'custom';

function periodPresetTabs(period: Exclude<CostTypePeriod, 'day'>): { key: PeriodPreset; label: string }[] {
  if (period === 'year') return [
    { key: 'current', label: '本年' },
    { key: 'previous', label: '去年' },
    { key: 'custom', label: '自定义' },
  ];
  return [
    { key: 'current', label: '本月' },
    { key: 'previous', label: '上月' },
    { key: 'custom', label: '自定义' },
  ];
}

function previousRangeFor(period: Exclude<CostTypePeriod, 'day'>): { start: string; end: string } {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  if (period === 'year') return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
  return { start: fmtLocalDate(new Date(y, m - 1, 1)), end: fmtLocalDate(new Date(y, m, 0)) };
}

type CostDimKey = 'date' | 'cost_type_1' | 'cost_type_2' | 'cost_type_3' | 'platform' | 'new_old_type' | 'target_type' | 'breakdown';
type CostDim = { key: CostDimKey; label: string };
type CostMetricKey = 'spend' | 'net_sales' | 'ratio';
type CostMetricColumn = { key: CostMetricKey; label: string; formatter: (value: number) => string };


const COST_TABLE_DIMS: CostDim[] = [
  { key: 'date', label: '日期维度' },
  { key: 'cost_type_1', label: '营销费用大类' },
  { key: 'cost_type_2', label: '营销费用一级分类' },
  { key: 'cost_type_3', label: '营销费用二级分类' },
  { key: 'platform', label: '效果营销平台' },
  { key: 'breakdown', label: '费用细分' },
];

const V_MARKETING_DIMS: CostDim[] = [
  { key: 'date', label: '日期' },
  { key: 'cost_type_1', label: '营销费用大类' },
  { key: 'cost_type_2', label: '营销费用一级分类' },
  { key: 'cost_type_3', label: '营销费用二级分类' },
  { key: 'platform', label: '效果营销平台' },
  { key: 'new_old_type', label: '广告新老客' },
  { key: 'target_type', label: '流量类型' },
  { key: 'breakdown', label: '费用细分' },
];

const V_AD_BREAKDOWN_DIMS: CostDim[] = [
  { key: 'date', label: '日期' },
  { key: 'platform', label: '效果营销平台' },
];

const V_MARKETING_DIM_OPTIONS = V_MARKETING_DIMS.map(dim => ({ value: dim.key, label: dim.label }));
const V_AD_BREAKDOWN_DIM_OPTIONS = V_AD_BREAKDOWN_DIMS.map(dim => ({ value: dim.key, label: dim.label }));
const COST_LABEL_FIELDS = ['cost_type_1', 'cost_type_2', 'cost_type_3', 'cost_breakdown_1', 'cost_breakdown_2', 'new_old_type', 'target_type', 'cost_breakdown_3'] as const;

const COST_TABLE_METRICS: CostMetricColumn[] = [
  { key: 'spend', label: '费用', formatter: fmtNum },
  { key: 'net_sales', label: '净销售额', formatter: fmtNum },
  { key: 'ratio', label: '费用占比', formatter: fmtPct },
];

const V_AD_BREAKDOWN_METRICS: CostMetricColumn[] = [
  { key: 'spend', label: '该层级费用', formatter: fmtNum },
  { key: 'ratio', label: '费用比例', formatter: fmtPct },
];

const costTableExportColumns = [
  ...COST_TABLE_DIMS.map(dim => ({ key: dim.key, label: dim.label })),
  ...COST_TABLE_METRICS.map(metric => ({ key: metric.key, label: metric.label })),
];

const tdNum: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const thBase: CSSProperties = { background: 'var(--surface2)', whiteSpace: 'nowrap' };

type LegacyCostTableRow = {
  date?: string;
  spend?: number;
  net_sales?: number;
  ratio?: number;
  is_total?: boolean;
};

function costRowID(level: number, value: string) {
  return `${level}:${encodeURIComponent(value || '-')}`;
}

function normalizeCostTableRows(rows: Array<CostVPivotRow | LegacyCostTableRow>): CostVPivotRow[] {
  if (rows.some(row => 'dimensions' in row && row.dimensions)) return rows as CostVPivotRow[];
  const normalized = rows.map((row, index): CostVPivotRow => {
    const legacy = row as LegacyCostTableRow;
    const date = legacy.date ?? '';
    const isTotal = Boolean(legacy.is_total) || date === '总计';
    return {
      dimensions: { date: isTotal ? '' : date },
      spend: legacy.spend ?? 0,
      net_sales: legacy.net_sales ?? 0,
      ratio: legacy.ratio ?? 0,
      grouping_id: isTotal ? COST_TABLE_DIMS.length + 1 : COST_TABLE_DIMS.length - 1,
      id: isTotal ? 'total' : costRowID(1, date || String(index)),
      parent_id: '',
      level: isTotal ? 0 : 1,
      has_children: false,
      is_total: isTotal,
    };
  });
  return normalized;
}

function Card({ title, children, extra }: { title: string; children: ReactNode; extra?: ReactNode }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
        {extra}
      </div>
      {children}
    </div>
  );
}

function SelectFilter({ label, field, pageFields, value, onChange }: { label: string; field: string; pageFields: readonly string[]; value: string; onChange: (v: string) => void }) {
  return (
    <label className="data-filter-field lg">
      <span className="data-filter-label">{label}</span>
      <ReportLabelSelect dataset="cost_type" field={field} pageFields={pageFields} includeAll value={value ? [value] : ['全部']} onChange={next => onChange(next[0] === '全部' ? '' : (next[0] ?? ''))} single width={180} />
    </label>
  );
}

function isVooglamSite(site: string): boolean {
  return site.trim().toLowerCase().startsWith('vooglam');
}

function dimValue(row: CostVPivotRow, key: CostDimKey): string {
  return row.dimensions?.[key] ?? '';
}

function rowDimText(row: CostVPivotRow, dim: CostDimKey, index: number): string {
  if (row.is_total) return index === 0 ? '总计' : '';
  if (row.level !== index + 1) return '';
  return dimValue(row, dim);
}

function defaultExpandedRows(rows: CostVPivotRow[]) {
  const expanded = new Set<string>();
  const total = rows.find(row => row.id === 'total' && row.has_children);
  if (total?.id) expanded.add(total.id);
  const firstDate = rows.find(row => !row.is_total && row.level === 1 && row.has_children);
  if (firstDate?.id) {
    expanded.add(firstDate.id);
    const firstType = rows.find(row => row.parent_id === firstDate.id && row.has_children);
    if (firstType?.id) expanded.add(firstType.id);
  }
  return expanded;
}

function costMetricValue(row: CostVPivotRow, key: CostMetricKey): number {
  if (key === 'net_sales') return row.net_sales;
  if (key === 'ratio') return row.ratio;
  return row.spend;
}

function costColumnsForExport(dims: CostDim[], metrics: CostMetricColumn[]) {
  return [
    ...dims.map(dim => ({ key: dim.key, label: dim.label })),
    ...metrics.map(metric => ({ key: metric.key, label: metric.label })),
  ];
}

function costDimsFromSelection(value: CostDimKey[], available: CostDim[]): CostDim[] {
  const map = new Map(available.map(dim => [dim.key, dim]));
  return value.map(key => map.get(key)).filter((dim): dim is CostDim => Boolean(dim));
}

type CostTreeControls = {
  expandAll: () => void;
  collapseAll: () => void;
};

function TreeExpansionActions({ controlsRef, loading, rows }: { controlsRef: MutableRefObject<CostTreeControls | null>; loading: boolean; rows: CostVPivotRow[] }) {
  const hasExpandableRows = rows.some(row => row.has_children);
  return (
    <>
      <button type="button" className="btn btn-outline btn-sm" onClick={() => controlsRef.current?.expandAll()} disabled={loading || !hasExpandableRows} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Maximize2 size={14} strokeWidth={2} />全部展开</button>
      <button type="button" className="btn btn-outline btn-sm" onClick={() => controlsRef.current?.collapseAll()} disabled={loading || !hasExpandableRows} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Minimize2 size={14} strokeWidth={2} />全部收起</button>
    </>
  );
}

function CostTreeTable({
  rows,
  loading,
  dims,
  metrics,
  minWidth = 1320,
  emptyText = '当前筛选条件下暂无花费类型数据',
  controlsRef,
}: {
  rows: CostVPivotRow[];
  loading: boolean;
  dims: CostDim[];
  metrics: CostMetricColumn[];
  minWidth?: number;
  emptyText?: string;
  controlsRef: MutableRefObject<CostTreeControls | null>;
}) {
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [sort, setSort] = useState<SortState<CostMetricKey>>(null);

  useEffect(() => {
    setExpanded(defaultExpandedRows(rows));
  }, [rows]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, CostVPivotRow[]>();
    for (const row of rows) {
      if (!row.parent_id) continue;
      const bucket = map.get(row.parent_id) ?? [];
      bucket.push(row);
      map.set(row.parent_id, bucket);
    }
    return map;
  }, [rows]);

  const toggleSort = useCallback((key: CostMetricKey) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);

  const sortedRoots = useMemo(() => {
    const roots = rows.filter(row => !row.parent_id);
    const totals = roots.filter(row => row.is_total);
    const dataRows = roots.filter(row => !row.is_total);
    return sort ? [...totals, ...sortRows(dataRows, sort, costMetricValue)] : roots;
  }, [rows, sort]);

  const sortedChildrenByParent = useMemo(() => {
    if (!sort) return childrenByParent;
    const next = new Map<string, CostVPivotRow[]>();
    childrenByParent.forEach((children, parentID) => {
      const totals = children.filter(row => row.is_total);
      const dataRows = children.filter(row => !row.is_total);
      next.set(parentID, [...totals, ...sortRows(dataRows, sort, costMetricValue)]);
    });
    return next;
  }, [childrenByParent, sort]);

  const visibleRows = useMemo(() => {
    const out: CostVPivotRow[] = [];
    const walk = (row: CostVPivotRow) => {
      out.push(row);
      if (!expanded.has(row.id)) return;
      for (const child of sortedChildrenByParent.get(row.id) ?? []) walk(child);
    };
    for (const root of sortedRoots) walk(root);
    return out;
  }, [expanded, sortedChildrenByParent, sortedRoots]);
  const expandableRowIDs = useMemo(() => rows.filter(row => row.has_children).map(row => row.id), [rows]);

  const toggleRow = (row: CostVPivotRow) => {
    if (!row.has_children) return;
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  const expandAll = useCallback(() => setExpanded(new Set(expandableRowIDs)), [expandableRowIDs]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  useEffect(() => {
    controlsRef.current = { expandAll, collapseAll };
    return () => { controlsRef.current = null; };
  }, [collapseAll, controlsRef, expandAll]);

  return (
    <div style={{ borderTop: '1px solid var(--border2)' }}>
      <div className="table-wrap" style={{ position: 'relative', maxHeight: 620, overflow: 'auto' }}>
        <table style={{ minWidth }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <tr>
            <th style={{ ...thBase, width: 360, minWidth: 360, maxWidth: 360 }}>
              <DimensionHeaderTooltip labels={dims.map(dim => dim.label)} />
            </th>
            {metrics.map(metric => (
              <SortableHeader
                key={metric.key}
                sortKey={metric.key}
                sort={sort}
                onSort={toggleSort}
                align="right"
                disabled={isSiteSensitiveMetric(metric.key) && userLevel < 2}
                style={{ ...thBase, ...tdNum }}
              >
                <MetricDescriptionTooltip label={metric.label} />
              </SortableHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? (
            <tr><td colSpan={metrics.length + 1}><TableLoadingCell /></td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={metrics.length + 1} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>{emptyText}</td></tr>
          ) : visibleRows.map((row, index) => {
            const activeDimIndex = row.is_total ? 0 : Math.max(0, row.level - 1);
            const activeDim = dims[activeDimIndex];
            const label = activeDim ? rowDimText(row, activeDim.key, activeDimIndex) || '-' : '-';
            return (
              <tr
                key={row.id || index}
                className={row.has_children ? 'tree-row-parent' : undefined}
                style={row.is_total ? { background: 'var(--surface)' } : undefined}
              >
                <td style={{ width: 360, minWidth: 360, maxWidth: 360 }}>
                  <span className="tree-cell-content" style={{ paddingLeft: activeDimIndex * 20 }}>
                    {row.has_children && (
                      <button
                        type="button"
                        className="tree-toggle-btn"
                        onClick={() => toggleRow(row)}
                        aria-label={expanded.has(row.id) ? '收起' : '展开'}
                        aria-expanded={expanded.has(row.id)}
                      >
                        {expanded.has(row.id) ? '−' : '+'}
                      </button>
                    )}
                    {!row.is_total && (row.has_children || activeDimIndex > 0) && activeDim && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 6px', borderRadius: 'var(--radius-xs)', background: 'var(--primary-bg)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                        {activeDim.label.replace(/维度$/, '')}
                      </span>
                    )}
                    <TruncatedValueTooltip value={label} />
                  </span>
                </td>
                {metrics.map(metric => (
                  <td key={metric.key} style={tdNum}>{formatSiteMetric(metric.key, costMetricValue(row, metric.key), metric.formatter, userLevel)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
        </table>
        {loading && rows.length > 0 && <TableLoadingOverlay />}
      </div>
    </div>
  );
}

function DimensionFilter({
  value,
  options,
  onChange,
}: {
  value: CostDimKey[];
  options: { value: string; label: string }[];
  onChange: (value: CostDimKey[]) => void;
}) {
  return (
    <div className="dimension-select-row" style={{ margin: '12px 16px 0' }}>
      <span className="dimension-select-label">动态维度</span>
      <MultiSelect
        options={options}
        value={value}
        onChange={(next) => {
          if (next.length === 0) return;
          onChange(next as CostDimKey[]);
        }}
        tagDisplay
        countLabelFormatter={(count) => `已选 ${count} 个维度`}
        style={{ minWidth: 320 }}
      />
    </div>
  );
}

function PieChart({ rows, height = 360 }: { rows: { name: string; value: number }[]; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (!chartRef.current) chartRef.current = echarts.init(ref.current);
    const data = rows.map(row => ({ name: row.name || '-', value: row.value || 0 }));
    chartRef.current.setOption({
      tooltip: { trigger: 'item', formatter: (pp: any) => `${pp.name}<br/>占比 ${(Number(pp.value || 0) * 100).toFixed(2)}%` },
      legend: { type: 'scroll', bottom: 0 },
      series: [{
        type: 'pie',
        radius: ['0%', '62%'],
        center: ['50%', '46%'],
        data,
        label: { show: true, formatter: (pp: any) => `${pp.name}: ${(Number(pp.value || 0) * 100).toFixed(2)}%`, fontSize: 11 },
        labelLine: { show: true },
        labelLayout: { hideOverlap: false },
      }],
    }, true);
  }, [rows]);

  useEffect(() => {
    const resize = () => chartRef.current?.resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => () => {
    chartRef.current?.dispose();
    chartRef.current = null;
  }, []);

  return <div ref={ref} style={{ width: '100%', height }} />;
}

function ChartCard({ title, rows, loading }: { title: string; rows: { name: string; value: number }[]; loading: boolean }) {
  const hasData = rows.some(row => row.value > 0);
  return (
    <Card title={title}>
      <div style={{ position: 'relative' }}>
        {loading && !hasData ? (
          <TableLoadingState />
        ) : !hasData ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>当前口径暂无数据</div>
        ) : (
          <PieChart rows={rows} />
        )}
        {loading && hasData && <TableLoadingOverlay />}
      </div>
    </Card>
  );
}

function breakdownRows(data: CostType2Breakdown | null) {
  if (!data) return [];
  return [
    { name: '效果广告营销费用', value: data.effect_ad },
    { name: '用户营销', value: data.user_mkt },
    { name: '红人营销', value: data.kol },
    { name: '联盟', value: data.alliance },
    { name: '其他', value: data.other },
    { name: '品牌营销费用', value: data.brand_mkt },
  ];
}

function pieRows(rows: CostPieRow[]) {
  return rows.map(row => ({ name: row.dim || '-', value: row.ratio }));
}

export default function SiteCostTypeMonitoring() {
  const { shopName: site } = useSiteShopName();
  const isVooglam = isVooglamSite(site);
  const [period, setPeriod] = useState<CostTypePeriod>('day');
  const init = rangeFor('day');
  const [startDate, setStartDate] = useState(init.start);
  const [endDate, setEndDate] = useState(init.end);
  const [dayPreset, setDayPreset] = useState('recent7');
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('current');

  const [costType1, setCostType1] = useState('');
  const [costType2, setCostType2] = useState('');
  const [costType3, setCostType3] = useState('');
  const [platform, setPlatform] = useState('');
  const [newOldType, setNewOldType] = useState('');
  const [targetType, setTargetType] = useState('');
  const [breakdown, setBreakdown] = useState('');
  const [vMarketingDims, setVMarketingDims] = useState<CostDimKey[]>(['date', 'cost_type_1']);
  const [vAdBreakdownDims, setVAdBreakdownDims] = useState<CostDimKey[]>(['date', 'platform']);

  const [tableRows, setTableRows] = useState<CostVPivotRow[]>([]);
  const [vMarketingRows, setVMarketingRows] = useState<CostVPivotRow[]>([]);
  const [vAdBreakdownRows, setVAdBreakdownRows] = useState<CostVPivotRow[]>([]);
  const [type1Rows, setType1Rows] = useState<CostPieRow[]>([]);
  const [type2Breakdown, setType2Breakdown] = useState<CostType2Breakdown | null>(null);
  const [effectAdRows, setEffectAdRows] = useState<CostPieRow[]>([]);
  const [paidChannelRows, setPaidChannelRows] = useState<CostPieRow[]>([]);
  const [primaryLoading, setPrimaryLoading] = useState(false);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [error, setError] = useState('');
  const [queryVersion, setQueryVersion] = useState(0);
  const [primaryExportSnapshot, setPrimaryExportSnapshot] = useState<{ params: CostTypeParams; dimensions: CostDimKey[] } | null>(null);
  const [secondaryExportSnapshot, setSecondaryExportSnapshot] = useState<{ params: CostTypeParams; dimensions: CostDimKey[] } | null>(null);
  const marketingTreeControlsRef = useRef<CostTreeControls | null>(null);
  const adBreakdownTreeControlsRef = useRef<CostTreeControls | null>(null);
  const costTreeControlsRef = useRef<CostTreeControls | null>(null);

  const tableParams = useMemo((): CostTypeParams => ({
    site,
    start_date: startDate,
    end_date: endDate,
    period,
    cost_type_1: costType1,
    cost_type_2: costType2,
    cost_type_3: costType3,
    platform,
    new_old_type: newOldType,
    target_type: targetType,
    breakdown,
  }), [site, startDate, endDate, period, costType1, costType2, costType3, platform, newOldType, targetType, breakdown]);

  const chartParams = useMemo((): CostTypeParams => ({
    site,
    start_date: startDate,
    end_date: endDate,
    period,
  }), [site, startDate, endDate, period]);

  const vMarketingParams = useMemo((): CostTypeParams => ({
    ...tableParams,
    dimensions: vMarketingDims.join(','),
  }), [tableParams, vMarketingDims]);

  const vAdBreakdownParams = useMemo((): CostTypeParams => ({
    site,
    start_date: startDate,
    end_date: endDate,
    period,
    platform,
    new_old_type: newOldType,
    target_type: targetType,
    breakdown,
    dimensions: vAdBreakdownDims.join(','),
  }), [site, startDate, endDate, period, platform, newOldType, targetType, breakdown, vAdBreakdownDims]);

  const vMarketingTableDims = useMemo(() => costDimsFromSelection(vMarketingDims, V_MARKETING_DIMS), [vMarketingDims]);
  const vAdBreakdownTableDims = useMemo(() => costDimsFromSelection(vAdBreakdownDims, V_AD_BREAKDOWN_DIMS), [vAdBreakdownDims]);

  const onPeriod = (p: CostTypePeriod) => {
    setPeriod(p);
    const next = rangeFor(p);
    setStartDate(next.start);
    setEndDate(next.end);
    if (p === 'day') setDayPreset('recent7');
    else setPeriodPreset('current');
  };

  const onPeriodPreset = (preset: PeriodPreset) => {
    if (period === 'day') return;
    setPeriodPreset(preset);
    if (preset === 'custom') return;
    const next = preset === 'previous' ? previousRangeFor(period) : rangeFor(period);
    setStartDate(next.start);
    setEndDate(next.end);
  };

  useEffect(() => {
    setCostType1('');
    setCostType2('');
    setCostType3('');
    setPlatform('');
    setNewOldType('');
    setTargetType('');
    setBreakdown('');
  }, [site]);

  const secondarySection = useLazySection(Boolean(site));

  const fetchPrimary = useCallback(async () => {
    if (!site) return;
    setPrimaryLoading(true);
    setError('');
    try {
      if (isVooglam) {
        const marketingRes = await siteDataApi.getCostTypeVMarketingShare(vMarketingParams);
        setVMarketingRows(normalizeCostTableRows(marketingRes.data?.list ?? []));
        setPrimaryExportSnapshot({ params: { ...vMarketingParams }, dimensions: [...vMarketingDims] });
        setTableRows([]);
        setType1Rows([]);
        setType2Breakdown(null);
        setEffectAdRows([]);
        setPaidChannelRows([]);
        return;
      }
      const tableRes = await siteDataApi.getCostTypeTable(tableParams);
      setTableRows(normalizeCostTableRows(tableRes.data?.list ?? []));
      setPrimaryExportSnapshot({ params: { ...tableParams }, dimensions: [] });
      setVMarketingRows([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
      setPrimaryExportSnapshot(null);
      setTableRows([]);
      setVMarketingRows([]);
    } finally {
      setPrimaryLoading(false);
    }
  }, [site, isVooglam, tableParams, vMarketingDims, vMarketingParams]);

  const fetchSecondary = useCallback(async () => {
    if (!site) return;
    setSecondaryLoading(true);
    setError('');
    try {
      if (isVooglam) {
        const adRes = await siteDataApi.getCostTypeVAdBreakdown(vAdBreakdownParams);
        setVAdBreakdownRows(normalizeCostTableRows(adRes.data?.list ?? []));
        setSecondaryExportSnapshot({ params: { ...vAdBreakdownParams }, dimensions: [...vAdBreakdownDims] });
        return;
      }
      const [type1Res, type2Res, effectRes, paidRes] = await Promise.all([
        siteDataApi.getCostTypeByType1(chartParams),
        siteDataApi.getCostTypeType2Breakdown(chartParams),
        siteDataApi.getCostTypeEffectAd(chartParams),
        siteDataApi.getCostTypePaidChannel(chartParams),
      ]);
      setType1Rows(type1Res.data?.list ?? []);
      setType2Breakdown(type2Res.data?.data ?? null);
      setEffectAdRows(effectRes.data?.list ?? []);
      setPaidChannelRows(paidRes.data?.list ?? []);
      setVAdBreakdownRows([]);
      setSecondaryExportSnapshot(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
      setSecondaryExportSnapshot(null);
      setVAdBreakdownRows([]);
      setType1Rows([]);
      setType2Breakdown(null);
      setEffectAdRows([]);
      setPaidChannelRows([]);
    } finally {
      setSecondaryLoading(false);
    }
  }, [site, isVooglam, chartParams, vAdBreakdownDims, vAdBreakdownParams]);

  useEffect(() => { void fetchPrimary(); }, [isVooglam, queryVersion, site]);
  useEffect(() => { if (secondarySection.ready) void fetchSecondary(); }, [isVooglam, queryVersion, secondarySection.ready, site]);

  const query = () => setQueryVersion(version => version + 1);
  useMarketingViewSection('page', {
    label: '页面筛选', state: { period, startDate, endDate, dayPreset, periodPreset, costType1, costType2, costType3, platform, newOldType, targetType, breakdown, vMarketingDims, vAdBreakdownDims },
    restore: value => { setPeriod(value.period); setStartDate(value.startDate); setEndDate(value.endDate); setDayPreset(value.dayPreset); setPeriodPreset(value.periodPreset); setCostType1(value.costType1); setCostType2(value.costType2); setCostType3(value.costType3); setPlatform(value.platform); setNewOldType(value.newOldType); setTargetType(value.targetType); setBreakdown(value.breakdown); setVMarketingDims(value.vMarketingDims); setVAdBreakdownDims(value.vAdBreakdownDims); },
    loading: primaryLoading || secondaryLoading, query: () => { query(); },
    resolve: value => { if (value.period === 'day' && value.dayPreset === 'today') { const date = fmtLocalDate(new Date()); return { ...value, dayPreset: 'custom' as const, startDate: date, endDate: date }; } const range = value.period === 'day' ? dayPresets().find(item => item.key === value.dayPreset && item.key !== 'custom') : value.periodPreset === 'custom' ? null : value.periodPreset === 'previous' ? previousRangeFor(value.period) : rangeFor(value.period); return range ? { ...value, startDate: range.start, endDate: range.end } : value; },
    summary: startDate + ' ～ ' + endDate + ' · ' + [costType1, costType2, costType3, platform, newOldType, targetType].filter(Boolean).join(' · '),
  });

  const reset = () => {
    const range = dayPresets().find(item => item.key === 'recent7') ?? dayPresets()[0];
    setPeriod('day');
    setStartDate(range.start);
    setEndDate(range.end);
    setDayPreset(range.key);
    setPeriodPreset('current');
    setCostType1('');
    setCostType2('');
    setCostType3('');
    setPlatform('');
    setNewOldType('');
    setTargetType('');
    setBreakdown('');
    setVMarketingDims(['date', 'cost_type_1']);
    setVAdBreakdownDims(['date', 'platform']);
    setQueryVersion(version => version + 1);
  };

  return (
    <div className="page-section visible">
      <div className="cost-type-sticky-filters">
        <div className="data-filter-card">
          <div className="data-filter-row time">
          <span className="data-filter-inline-label">日期维度</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {PERIOD_TABS.map(tab => (
              <div
                key={tab.key}
                className={`page-tab${period === tab.key ? ' active' : ''}`}
                onClick={() => onPeriod(tab.key)}
              >
                {tab.label}
              </div>
            ))}
          </div>

          {period === 'day' ? (
            <>
              <span className="data-filter-inline-label">日期范围</span>
              <div className="page-tabs" style={{ marginBottom: 0 }}>
                {dayPresets().map(preset => {
                  const active = dayPreset === preset.key;
                  return (
                    <div
                      key={preset.key}
                      className={`page-tab${active ? ' active' : ''}`}
                      onClick={() => {
                        setDayPreset(preset.key);
                        if (preset.key !== 'custom') {
                          setStartDate(preset.start);
                          setEndDate(preset.end);
                        }
                      }}
                    >
                      {preset.label}
                    </div>
                  );
                })}
              </div>

              {dayPreset === 'custom' && <MarketingDateRangePicker start={startDate} end={endDate} max={fmtLocalDate(new Date())} onChange={({ start, end }) => { setStartDate(start); setEndDate(end); }} />}
            </>
          ) : (
            <>
              <span className="data-filter-inline-label">日期范围</span>
              <div className="page-tabs" style={{ marginBottom: 0 }}>
                {periodPresetTabs(period).map(preset => (
                  <div
                    key={preset.key}
                    className={`page-tab${periodPreset === preset.key ? ' active' : ''}`}
                    onClick={() => onPeriodPreset(preset.key)}
                  >
                    {preset.label}
                  </div>
                ))}
              </div>
              {periodPreset === 'custom' && <MarketingDateRangePicker grain={period} start={startDate} end={endDate} max={fmtLocalDate(new Date())} onChange={({ start, end }) => { setStartDate(start); setEndDate(end); }} />}
            </>
          )}
          </div>
        </div>

        <div className="data-filter-card">
          <div className="data-filter-row compact">
            <SelectFilter label="营销费用大类" field="cost_type_1" pageFields={COST_LABEL_FIELDS} value={costType1} onChange={setCostType1} />
            <SelectFilter label="营销费用一级分类" field="cost_type_2" pageFields={COST_LABEL_FIELDS} value={costType2} onChange={setCostType2} />
            <SelectFilter label="营销费用二级分类" field="cost_type_3" pageFields={COST_LABEL_FIELDS} value={costType3} onChange={setCostType3} />
            <SelectFilter label="效果营销平台" field="cost_breakdown_1" pageFields={COST_LABEL_FIELDS} value={platform} onChange={setPlatform} />
            {isVooglam && <SelectFilter label="广告新老客" field="new_old_type" pageFields={COST_LABEL_FIELDS} value={newOldType} onChange={setNewOldType} />}
            {isVooglam && <SelectFilter label="流量类型" field="target_type" pageFields={COST_LABEL_FIELDS} value={targetType} onChange={setTargetType} />}
            <SelectFilter label="费用细分" field={isVooglam ? 'cost_breakdown_3' : 'cost_breakdown_2'} pageFields={COST_LABEL_FIELDS} value={breakdown} onChange={setBreakdown} />
            <DashboardQueryActions loading={primaryLoading || secondaryLoading} onReset={reset} onQuery={query} />
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: 'var(--red-bg)', color: 'var(--red)', fontSize: 13 }}>{error}</div>
      )}

      {isVooglam ? (
        <>
          <Card
            title="整站营销费用占比"
            extra={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TreeExpansionActions controlsRef={marketingTreeControlsRef} loading={primaryLoading} rows={vMarketingRows} /><ReportExportButton reportKey="site_cost_type_marketing_share" title="V站整站营销费用占比" fileName={`V站整站营销费用占比_${primaryExportSnapshot?.params.start_date ?? ''}_${primaryExportSnapshot?.params.end_date ?? ''}.csv`} params={primaryExportSnapshot?.params ?? {}} columns={costColumnsForExport(costDimsFromSelection(primaryExportSnapshot?.dimensions ?? [], V_MARKETING_DIMS), COST_TABLE_METRICS)} disabled={!primaryExportSnapshot || primaryLoading} /></div>}
          >
            <DimensionFilter
              value={vMarketingDims}
              options={V_MARKETING_DIM_OPTIONS}
              onChange={setVMarketingDims}
            />
            <CostTreeTable
              rows={vMarketingRows}
              loading={primaryLoading}
              dims={vMarketingTableDims}
              metrics={COST_TABLE_METRICS}
              minWidth={980}
              emptyText="当前筛选条件下暂无整站营销费用占比数据"
              controlsRef={marketingTreeControlsRef}
            />
          </Card>

          <div ref={secondarySection.ref}>
          <Card
            title="广告营销费用细分比例"
            extra={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TreeExpansionActions controlsRef={adBreakdownTreeControlsRef} loading={secondaryLoading} rows={vAdBreakdownRows} /><ReportExportButton reportKey="site_cost_type_ad_breakdown" title="V站广告营销费用细分比例" fileName={`V站广告营销费用细分比例_${secondaryExportSnapshot?.params.start_date ?? ''}_${secondaryExportSnapshot?.params.end_date ?? ''}.csv`} params={secondaryExportSnapshot?.params ?? {}} columns={costColumnsForExport(costDimsFromSelection(secondaryExportSnapshot?.dimensions ?? [], V_AD_BREAKDOWN_DIMS), V_AD_BREAKDOWN_METRICS)} disabled={!secondaryExportSnapshot || secondaryLoading} /></div>}
          >
            <div style={{ padding: '12px 16px 0', color: 'var(--text2)', fontSize: 12 }}>
              该看板只受日期、效果营销平台、广告新老客、流量类型、费用细分筛选控制。
            </div>
            <DimensionFilter
              value={vAdBreakdownDims}
              options={V_AD_BREAKDOWN_DIM_OPTIONS}
              onChange={setVAdBreakdownDims}
            />
            <CostTreeTable
              rows={vAdBreakdownRows}
              loading={secondaryLoading}
              dims={vAdBreakdownTableDims}
              metrics={V_AD_BREAKDOWN_METRICS}
              minWidth={900}
              emptyText="当前筛选条件下暂无广告营销费用细分比例数据"
              controlsRef={adBreakdownTreeControlsRef}
            />
          </Card>
          </div>
        </>
      ) : (
        <>
          <Card
            title="花费监控看板"
            extra={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TreeExpansionActions controlsRef={costTreeControlsRef} loading={primaryLoading} rows={tableRows} /><ReportExportButton reportKey="site_cost_type_table" title="花费监控看板" fileName={`花费监控看板_${primaryExportSnapshot?.params.start_date ?? ''}_${primaryExportSnapshot?.params.end_date ?? ''}.csv`} params={primaryExportSnapshot?.params ?? {}} columns={costTableExportColumns} disabled={!primaryExportSnapshot || primaryLoading} /></div>}
          >
            <CostTreeTable rows={tableRows} loading={primaryLoading} dims={COST_TABLE_DIMS} metrics={COST_TABLE_METRICS} controlsRef={costTreeControlsRef} />
          </Card>

          <div ref={secondarySection.ref} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
            <ChartCard title="整站营销占比(含品牌部)-按预算归属划分" rows={pieRows(type1Rows)} loading={secondaryLoading} />
            <ChartCard title="整站营销占比(含品牌部)-明细表" rows={breakdownRows(type2Breakdown)} loading={secondaryLoading} />
            <ChartCard title="效果广告营销占比(含付费渠道营销费用+APP广告费用+SEO广告费用)" rows={pieRows(effectAdRows)} loading={secondaryLoading} />
            <ChartCard title="付费渠道营销占比" rows={pieRows(paidChannelRows)} loading={secondaryLoading} />
          </div>
        </>
      )}
    </div>
  );
}
